import { createElement, lazy, useState, type ComponentType } from 'react';

// React.lazy plus a preload() that main.tsx awaits for the initial route
// before mounting. React.lazy always suspends on its first render — even when
// the chunk is already in memory — and createRoot discards the prerendered
// #root markup on mount, so without this every first page load swapped the
// prerendered content for the Suspense spinner until the chunk arrived.
//
// Once preload() has resolved, new instances render the loaded component
// directly and never suspend. The direct-vs-lazy choice is made once per
// mounted instance (useState initializer), so a page that started out lazy
// is never remounted (and never loses its state) when the module lands.
export type PreloadableComponent<P> = ComponentType<P> & { preload: () => Promise<unknown> };

export function lazyWithPreload<P extends object>(
  factory: () => Promise<{ default: ComponentType<P> }>
): PreloadableComponent<P> {
  let loaded: ComponentType<P> | undefined;
  let pending: Promise<{ default: ComponentType<P> }> | undefined;

  // A failed load is forgotten, so a failed preload doesn't poison the later
  // React.lazy render — that render retries the import as it always did.
  const load = () => {
    pending ??= factory().then(
      (module) => {
        loaded = module.default;
        return module;
      },
      (error) => {
        pending = undefined;
        throw error;
      }
    );
    return pending;
  };

  const LazyComponent = lazy(load);

  const Preloadable = (props: P) => {
    const [Impl] = useState<ComponentType<P>>(() => loaded ?? (LazyComponent as unknown as ComponentType<P>));
    return createElement(Impl, props);
  };
  Preloadable.preload = load;

  return Preloadable;
}
